/**
 * usePublicPath — small hook that reads window.location.pathname
 * once and exposes a setter so screens can navigate via the History
 * API without dragging in react-router-dom.
 *
 * EffectiveSLP's app shell is a state machine keyed on session +
 * onboarding-seen; every path is auth-gated. The `publicPath`
 * resolution feeds `App.tsx`'s screen switch. Anything not matched
 * falls back to `/caseload` (the post-sign-in landing screen).
 */
import { useEffect, useState } from 'react';

export type PublicPath =
  | '/'
  | '/caseload'
  | '/auth'
  | '/auth/callback'
  | '/handoff/demo'
  | '/handoff/:tokenId'
  | '/handoff/:tokenId/practice'
  | '/upgrade'
  | '/settings'
  | '/student/new'
  | '/student/:id'
  | '/student/:id/edit'
  | '/student/:id/goal/new'
  | '/student/:id/goal/:goalId'
  | '/student/:id/session/new'
  | '/session/:sessionId'
  | '/session/:sessionId/notes'
  | '/parent/sign-in'
  | '/parent/consent'
  | '/parent/dashboard'
  | '/parent/goals'
  | '/parent/thread/:threadId'
  | '/not-found';

function readPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
}

function classifyPath(path: string): PublicPath {
  if (path === '/' || path === '') return '/caseload';
  if (path === '/caseload') return '/caseload';
  if (path === '/auth') return '/auth';
  if (path === '/auth/callback') return '/auth/callback';
  // /handoff/demo is a public marketing/demo surface — it must be
  // matched BEFORE the catch-all /handoff/:tokenId regex below,
  // because "demo" would otherwise parse as a token id and route
  // the prospect into the live resolver (which would 404, but
  // better not to make them wait for a network round-trip).
  if (path === '/handoff/demo') return '/handoff/demo';
  const handoffPath = path.match(/^\/handoff\/([A-Za-z0-9_-]+)$/);
  if (handoffPath) return '/handoff/:tokenId';
  const practicePath = path.match(/^\/handoff\/([A-Za-z0-9_-]+)\/practice$/);
  if (practicePath) return '/handoff/:tokenId/practice';
  if (path === '/upgrade') return '/upgrade';
  if (path === '/settings') return '/settings';
  if (path === '/student/new') return '/student/new';
  if (/^\/student\/[^/]+\/edit$/.test(path)) return '/student/:id/edit';
  if (/^\/student\/[^/]+\/goal\/new$/.test(path)) return '/student/:id/goal/new';
  if (/^\/student\/[^/]+\/goal\/[^/]+$/.test(path)) return '/student/:id/goal/:goalId';
  if (/^\/student\/[^/]+\/session\/new$/.test(path)) return '/student/:id/session/new';
  if (/^\/session\/[^/]+\/notes$/.test(path)) return '/session/:sessionId/notes';
  if (/^\/session\/[^/]+$/.test(path)) return '/session/:sessionId';
  if (/^\/student\/[^/]+$/.test(path)) return '/student/:id';
  if (path === '/parent/sign-in') return '/parent/sign-in';
  if (path === '/parent/consent') return '/parent/consent';
  if (path === '/parent/dashboard') return '/parent/dashboard';
  if (path === '/parent/goals') return '/parent/goals';
  if (/^\/parent\/thread\/[^/]+$/.test(path)) return '/parent/thread/:threadId';
  if (path === '/') return '/';
  return '/not-found';
}

export function usePublicPath(): {
  publicPath: PublicPath;
  navigate: (path: string) => void;
  replace: (path: string) => void;
} {
  const [publicPath, setPublicPath] = useState<PublicPath>(() =>
    classifyPath(readPath()),
  );

  useEffect(() => {
    function onPop(): void {
      setPublicPath(classifyPath(readPath()));
    }
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  function navigate(path: string): void {
    window.history.pushState({}, '', path);
    setPublicPath(classifyPath(path));
  }

  function replace(path: string): void {
    window.history.replaceState({}, '', path);
    setPublicPath(classifyPath(path));
  }

  return { publicPath, navigate, replace };
}
